/**
 * A label placed on a pano: where it is, what it says, how it is drawn on the canvas, and its marker on the minimap.
 * Its hover card belongs to Canvas, which draws it from the label's properties.
 */

import * as turf from '@turf/turf';
import i18next from 'i18next';
import { util } from '../../common/utilities.js';
import '../../common/pano-viewer/panoUtilities.js';
import '../../common/utilitiesSidewalk.js';
/** @typedef {import('../canvas/CanvasFrame.js').CanvasFrame} CanvasFrame */
/** @typedef {import('../navigation/Minimap.js').Minimap} Minimap */
/** @typedef {import('../navigation/Minimap.js').MinimapMarker} MinimapMarker */
/** @typedef {import('../../common/pano-viewer/PanoStore.js').PanoStore} PanoStore */

/**
 * The lat/lng estimator's constants, owned by the backend (PanoDataService.LatLngEstimation).
 * @typedef {object} LatLngEstimation
 * @property {number} cameraHeightM
 * @property {number} blendDeg
 * @property {number} maxDistanceM
 */

export class Label {
  className = 'Label'; // Read by Canvas.js for type dispatch (`item.className === 'Label'`).

  #frame;
  #latLngEstimation;

  /** @type {?MinimapMarker} This label's icon on the minimap, once LabelContainer has placed it. */
  #minimapMarker = null;

  // Which era of the user's work this label belongs to on the minimap: 'current' (this mission), 'prior' (an earlier
  // mission in this region), or 'outdated' (placed on imagery that has since been replaced, #4945). See minimapEra.
  /** @type {'current'|'prior'|'outdated'} */
  #minimapEra = 'current';

  // True while the "My earlier labels" legend toggle hides this label's minimap marker. Kept apart from the deleted /
  // visibility status so render() can't show the marker again the next time the pano is drawn.
  #minimapSuppressed = false;

  // Size the label-type icons are rasterized to before being drawn (see preloadIcons). The label canvas renders at
  // its on-screen size times the device pixel ratio, so the icon tops out around 76 device pixels on a HiDPI
  // display (util.LABEL_ICON_MAX_SCREEN_DIAMETER at devicePixelRatio 2); rasterizing below that is what left the
  // icon looking soft. Kept well above that ceiling so the raster is always downscaled, never stretched.
  static #ICON_RASTER_SIZE = 128;

  // On-screen size of the labeling cursor, in CSS px. Canvas.js centers its hotspot on this. This bitmap does not
  // scale with the tool, so it is also the ceiling a placed icon may grow to — see util.labelIconRadius (#4838).
  static CURSOR_ICON_SIZE = 38;

  static #cursorUrlCache = new Map();

  // Resolves when this label's crop reaches the server; null until a submission hands us an id. See cropUploaded().
  #cropUpload = null;

  // The properties the labeler edits after placing a label, which a submission sends. Set only through edit().
  static #EDITABLE = new Set(['severity', 'tagIds', 'description']);

  /** @type {Array<() => void>} */
  #editListeners = [];

  #properties = {
    labelId: 'DefaultValue',
    auditTaskId: undefined,
    missionId: undefined,
    labelType: undefined,
    originalCanvasXY: undefined,
    originalCanvasFrame: undefined, // The logical frame originalCanvasXY is in (#5085); stored with the label.
    currCanvasXY: undefined,
    panoXY: undefined,
    originalPov: undefined,
    povOfLabelIfCentered: undefined,
    labelLat: undefined,
    labelLng: undefined,
    latLngComputationMethod: undefined,
    panoId: undefined,
    panoLat: undefined,
    panoLng: undefined,
    cameraHeading: undefined,
    panoWidth: undefined,
    panoHeight: undefined,
    tagIds: [],
    severity: null,
    tutorial: null,
    tutorialLabelNumber: undefined,
    temporaryLabelId: null,
    description: null,
    crop: undefined,
    fromOutdatedImagery: false,
  };

  #status = {
    deleted: false,
    hoverInfoVisibility: 'visible',
    visibility: 'visible',
  };

  /**
   * @param {object} params - Initial label property values (only keys present in #properties are copied).
   * @param {CanvasFrame} frame - The frame the label is drawn in and hit-tested against.
   * @param {LatLngEstimation} latLngEstimation - The estimator's constants, for toLatLng.
   * @param {PanoStore} panoStore - Where a new label's pano metadata comes from.
   */
  constructor(params, frame, latLngEstimation, panoStore) {
    this.#frame = frame;
    this.#latLngEstimation = latLngEstimation;
    for (const attrName in params) {
      if (Object.hasOwn(params, attrName) && Object.hasOwn(this.#properties, attrName)) {
        this.#properties[attrName] = params[attrName];
      }
    }

    // Save pano data and calculate pano_x/y if the label is new.
    if (this.#properties.panoXY === undefined) {
      const panoData = panoStore.getPanoData(this.#properties.panoId).getProperties();

      this.#properties.panoWidth = panoData.width;
      this.#properties.panoHeight = panoData.height;
      this.#properties.cameraHeading = panoData.cameraHeading;
      this.#properties.panoLat = panoData.lat;
      this.#properties.panoLng = panoData.lng;
      this.#properties.panoXY = util.pano.povToPanoCoord(
        this.#properties.povOfLabelIfCentered, this.#properties.cameraHeading,
        this.#properties.panoWidth, this.#properties.panoHeight,
      );
    }
  }

  /**
   * Classifies a label for the minimap (#4945): the current mission's labels are the user's live work; labels from
   * an earlier mission in the region are an earlier pass; labels whose audit task is flagged outdated_imagery were
   * placed on imagery that has since been replaced, so during a re-audit they are the previous era's record.
   *
   * Era is keyed on mission rather than audit task because that is the boundary the user experiences (the
   * mission-complete modal), and it matches the rule the marker click already uses: only current-mission labels can
   * be returned to. The mission test comes first: a label placed in the mission the user is on right now is live work
   * even if its audit task has since been flagged outdated_imagery (a mission spans several tasks, so one can be
   * flagged mid-mission by the nightly freshness sync). A label with no mission id, or with no mission loaded to
   * compare against, is never 'prior', so nothing is dimmed by accident; only the imagery flag can dim it.
   *
   * @param {{missionId?: number, fromOutdatedImagery?: boolean}} props - The label's properties.
   * @param {?number} currentMissionId - The mission the user is on, or null if unknown.
   * @returns {'current'|'prior'|'outdated'}
   */
  static minimapEra(props, currentMissionId) {
    const { missionId } = props;
    const known = (id) => id !== null && id !== undefined;
    const bothKnown = known(currentMissionId) && known(missionId);
    if (bothKnown && missionId === currentMissionId) return 'current';
    if (props.fromOutdatedImagery) return 'outdated';
    if (bothKnown) return 'prior';
    return 'current';
  }

  getMinimapEra() {
    return this.#minimapEra;
  }

  /**
   * Puts the label on the minimap, styled for its era (#4945). The tutorial's example labels use createMinimapMarker.
   * @param {Minimap} minimap
   * @param {?number} currentMissionId - The mission the user is on, which decides the era.
   * @param {() => void} onClick - What clicking the marker does. Only a current-era marker is clickable, since the
   *   click returns to the label and that only works within the current mission (#2561).
   */
  placeOnMinimap(minimap, currentMissionId, onClick) {
    this.#minimapEra = Label.minimapEra(this.#properties, currentMissionId);
    this.#minimapMarker = Label.createMinimapMarker(
      minimap, this.#properties.labelType, this.toLatLng(), this.#minimapEra, onClick,
    );
  }

  /**
   * Re-derives the era against the mission now current and restyles the marker. Called when the mission changes: the
   * labels just placed become the previous pass, and a resumed mission's labels come back as current work. Whether the
   * marker is on the map is the legend toggle's call (LabelContainer.refreshMinimapEras re-applies it after this).
   * @param {?number} currentMissionId - The mission the user is on now.
   */
  refreshMinimapEra(currentMissionId) {
    const era = Label.minimapEra(this.#properties, currentMissionId);
    if (era === this.#minimapEra) return;
    this.#minimapEra = era;
    if (this.#minimapMarker) {
      Label.styleMinimapMarker(this.#minimapMarker, this.#properties.labelType, era);
    }
  }

  /**
   * Hides or shows this label's minimap marker for the "My earlier labels" legend toggle. Only the marker: the label
   * itself stays live, is still drawn on its pano, and still counts.
   * @param {boolean} suppressed
   */
  setMinimapMarkerSuppressed(suppressed) {
    this.#minimapSuppressed = suppressed;
    if (this.#minimapMarker) this.#minimapMarker.setVisible(!suppressed && !this.isDeleted());
  }

  isMinimapMarkerSuppressed() {
    return this.#minimapSuppressed;
  }

  /** @returns {?MinimapMarker} This label's minimap marker. */
  getMinimapMarker() {
    return this.#minimapMarker;
  }

  // Some functions for easy access to commonly accessed properties.
  getLabelId() {
    return this.#properties.labelId;
  }

  getLabelType() {
    return this.#properties.labelType;
  }

  getPanoId() {
    return this.#properties.panoId;
  }

  /**
   * Returns the coordinate of the label.
   * @returns {{x: number, y: number}}
   */
  getCanvasXY() {
    return this.#properties.currCanvasXY;
  }

  /**
   * Returns a deep copy of the properties object, so callers can't mutate the label's internal state directly.
   * @returns {object}
   */
  getProperties() {
    return structuredClone(this.#properties);
  }

  getProperty(propName) {
    return (propName in this.#properties) ? this.#properties[propName] : false;
  }

  setProperty(key, value) {
    if (Label.#EDITABLE.has(key)) throw new Error(`Label.setProperty: '${key}' is set through edit().`);
    this.#properties[key] = value;
  }

  /**
   * Sets the labeler's edits (severity, tagIds, description) and tells onEdited's listeners, so the label is sent
   * with the next submission whoever made the edit.
   * @param {{severity?: ?number, tagIds?: number[], description?: ?string}} changes
   */
  edit(changes) {
    // Checked before any write, so a bad key can't leave the label half-edited and unfiled.
    const badKey = Object.keys(changes).find((key) => !Label.#EDITABLE.has(key));
    if (badKey) throw new Error(`Label.edit: '${badKey}' is not an editable property.`);
    Object.assign(this.#properties, changes);
    this.#editListeners.forEach((listener) => listener());
  }

  /** @param {() => void} listener - Called after every edit(). */
  onEdited(listener) {
    this.#editListeners.push(listener);
  }

  getStatus(key) {
    return this.#status[key];
  }

  isDeleted() {
    return this.#status.deleted;
  }

  isVisible() {
    return this.#status.visibility === 'visible';
  }

  setVisibility(visibility) {
    this.#status.visibility = visibility;
  }

  /**
   * Set status. Deals with special cases for the various status values that have a limited set of values.
   * @param {string} key
   * @param {*} value
   */
  setStatus(key, value) {
    if (key in this.#status) {
      if (key === 'visibility' && (value === 'visible' || value === 'hidden')) {
        this.setVisibility(value);
      } else if (key === 'hoverInfoVisibility' && (value === 'visible' || value === 'hidden')) {
        this.setHoverInfoVisibility(value);
      } else if (key === 'deleted' && typeof value === 'boolean') {
        this.#status[key] = value;
      } else if (key === 'severity') {
        this.#status[key] = value;
      }
    }
  }

  /**
   * Set the visibility of the hover info.
   * @param {string} visibility - 'visible' or 'hidden'.
   * @returns {Label} this, for chaining.
   */
  setHoverInfoVisibility(visibility) {
    if (visibility === 'visible' || visibility === 'hidden') {
      this.#status.hoverInfoVisibility = visibility;
    }
    return this;
  }

  getHoverInfoVisibility() {
    return this.#status.hoverInfoVisibility;
  }

  /**
   * Check if this label is under the cursor.
   * @param {number} x
   * @param {number} y
   * @returns {boolean}
   */
  isOn(x, y) {
    // Sized to the drawn icon (util.labelHitMargin), so the click target is never smaller than the icon under it.
    const margin = this.#frame.hitMargin;
    return !this.#status.deleted
      && this.#status.visibility === 'visible'
      && this.#properties.currCanvasXY
      && x < this.#properties.currCanvasXY.x + margin
      && x > this.#properties.currCanvasXY.x - margin
      && y < this.#properties.currCanvasXY.y + margin
      && y > this.#properties.currCanvasXY.y - margin;
  }

  /**
   * Remove the label (it does not actually remove, but hides the label and set its status to 'deleted').
   */
  remove() {
    this.setStatus('deleted', true);
    this.setStatus('visibility', 'hidden');
  }

  /**
   * Renders this label on a canvas.
   * @param {CanvasRenderingContext2D} ctx
   * @param {{heading: number, pitch: number, zoom: number}} pov
   * @param {boolean} [faded=false] - Whether its dialog is open: the marker sits exactly on the feature being rated,
   *   so fading it lets the user see that feature while they rate it; the dialog's tail still marks the spot.
   * @returns {Label} this.
   */
  render(ctx, pov, faded = false) {
    if (!this.#status.deleted && this.#status.visibility === 'visible') {
      // Update the coordinates of the label on the canvas.
      this.#properties.currCanvasXY = util.pano.centeredPovToCanvasCoord(
        this.#properties.povOfLabelIfCentered, pov,
        this.#frame.width, this.#frame.height, this.#frame.iconRadius, this.#frame.renderedHFov(pov.zoom),
      );

      // Draw the label icon if it's in the visible part of the pano.
      if (this.#properties.currCanvasXY) {
        if (faded) {
          ctx.save();
          ctx.globalAlpha = 0.3;
        }
        Label.renderLabelIcon(
          ctx, this.#properties.labelType, this.#properties.currCanvasXY.x, this.#properties.currCanvasXY.y,
          this.#frame.iconRadius,
        );

        // Only render severity warning if there's a severity option.
        if (util.misc.labelTypeHasSeverity(this.#properties.labelType) && this.#properties.severity === null) {
          this.#showSeverityAlert(ctx);
        }
        if (faded) ctx.restore();
      }
    }

    if (this.#minimapMarker) this.#minimapMarker.setVisible(!this.isDeleted() && !this.#minimapSuppressed);
    return this;
  }

  /**
   * Renders a question mark if a label has an unmarked severity.
   * @param {CanvasRenderingContext2D} ctx - Rendering tool for severity (2D context).
   */
  #showSeverityAlert(ctx) {
    const x = this.#properties.currCanvasXY.x;
    const y = this.#properties.currCanvasXY.y;
    // Canvas resolves neither CSS variables nor --ui-scale, so the design tokens are read off :root. (No scaling
    // wanted here regardless: this canvas keeps its fixed logical size and is scaled up by the browser.)
    const rootStyle = getComputedStyle(document.documentElement);
    // The badge's placement and size are authored against the base icon radius, so they scale with the icon and it
    // stays pinned to the icon's upper-left however the icon is sized (#4838).
    const k = util.labelIconScale(this.#frame.iconRadius);

    // Draws circle. The same --color-error-200 the hover card's unrated chip uses, so the "?" on the canvas and the
    // "?" on the card that describes it are one mark in two places rather than two different reds (#4731).
    ctx.beginPath();
    // Trimmed: a custom property's value keeps the whitespace after the colon, and an unparseable fillStyle is
    // silently ignored rather than throwing — the circle would just keep whatever color was set last.
    ctx.fillStyle = rootStyle.getPropertyValue('--color-error-200').trim();
    ctx.ellipse(x - 15 * k, y - 10.5 * k, 8 * k, 8 * k, 0, 0, 2 * Math.PI);
    ctx.fill();
    ctx.closePath();

    // Draws text.
    ctx.beginPath();
    ctx.font = `400 ${12 * k}px ${rootStyle.getPropertyValue('--font-primary')}`;
    ctx.fillStyle = 'rgb(255, 255, 255)';
    ctx.fillText('?', x - 17.5 * k, y - 6 * k);
    ctx.closePath();
  }

  /**
   * Get the label's estimated latlng position.
   *
   * The bearing to the label is exact projection geometry (its centered POV), and the distance below the horizon comes
   * from flat-ground cotangent geometry with a linear tail near the horizon — the same computation as the server's
   * PanoDataService.toLatLng, with the constants injected from the backend (the page's latLngEstimation) so the two
   * can't drift. Both depend only on the label's angular position, so the estimate is independent of both the pano's
   * resolution and the label's type.
   *
   * The camera height and blend angle are fitted values, not tuning knobs — PanoDataService.LatLngEstimation is where
   * they live and where their derivation is documented, and the research behind them (held-out splits, the falsified
   * alternatives, the reports) is in https://github.com/ProjectSidewalk/label-latlng-estimation. The in-repo summary,
   * including the parity fixture this must keep reproducing, is docs/label-latlng-estimation.md.
   *
   * @returns {{lat: number, lng: number, latLngComputationMethod: string}}
   */
  toLatLng() {
    if (!this.#properties.labelLat) {
      // Estimate the latlng point from the pano position and the label's bearing and depression angle when point
      // cloud data isn't available.
      const params = this.#latLngEstimation;
      const pov = this.getProperty('povOfLabelIfCentered');
      const depressionDeg = -pov.pitch;
      const heightM = params.cameraHeightM;
      const blendRad = params.blendDeg * Math.PI / 180;

      let estDistanceM;
      if (depressionDeg >= params.blendDeg) {
        estDistanceM = heightM / Math.tan(depressionDeg * Math.PI / 180);
      } else {
        // The tail's slope is the cotangent's derivative at the blend angle, so value and slope match at the handoff.
        // Above the horizon the answer is the horizon's, keeping the estimate bounded for any input.
        const tailM = heightM / Math.tan(blendRad)
          + heightM * (Math.PI / 180) / (Math.sin(blendRad) ** 2) * (params.blendDeg - Math.max(depressionDeg, 0));
        estDistanceM = Math.min(tailM, params.maxDistanceM);
      }

      const startPoint = turf.point([this.getProperty('panoLng'), this.getProperty('panoLat')]);
      const destination = turf.destination(startPoint, estDistanceM / 1000, pov.heading, { units: 'kilometers' });
      const latlng = {
        lat: destination.geometry.coordinates[1],
        lng: destination.geometry.coordinates[0],
        latLngComputationMethod: 'approximation3',
      };
      this.setProperty('labelLat', latlng.lat);
      this.setProperty('labelLng', latlng.lng);
      this.setProperty('latLngComputationMethod', latlng.latLngComputationMethod);
      return latlng;
    } else {
      return {
        lat: this.getProperty('labelLat'),
        lng: this.getProperty('labelLng'),
        latLngComputationMethod: this.getProperty('latLngComputationMethod'),
      };
    }
  }

  /**
   * Records the label's official id from the server and saves a screenshot named crop_<labelId>.png. The crops are
   * stored in subdirs /<city-id>/<label-type>.
   *
   * The id is set straight away rather than only on the path where a crop exists: it is what the rest of the app
   * keys off (the share permalink, most obviously), and it is known and correct whether or not the canvas has
   * finished producing a crop to go with it.
   *
   * @param {number} labelId
   * @returns {Promise<void>} Resolves once the crop has been uploaded, or given up on.
   */
  updateLabelIdAndUploadCrop(labelId) {
    this.setProperty('labelId', labelId);
    this.#cropUpload = this.#uploadCrop(labelId);
    return this.#cropUpload;
  }

  /**
   * Resolves once this label's crop has reached the server, or immediately if no upload was ever started.
   *
   * Sharing waits on this. /label/:id/image falls back to a fetched Street View still — or the branded placeholder —
   * when no crop is on disk, and caches whatever it built there permanently, so a link handed out in the seconds
   * before the crop lands would keep the wrong preview for good.
   *
   * @returns {Promise<void>}
   */
  cropUploaded() {
    return this.#cropUpload ?? Promise.resolve();
  }

  /**
   * Uploads the crop, retrying once if the canvas hasn't produced it yet.
   * @param {number} labelId
   * @returns {Promise<void>}
   */
  async #uploadCrop(labelId) {
    if (!this.getProperty('crop')) {
      console.log('No crop found to upload, retrying in 3 seconds.');
      await new Promise((resolve) => setTimeout(resolve, 3000));
      if (!this.getProperty('crop')) {
        console.log('No crop found to upload after 2 attempts.');
        return;
      }
    }

    // Upload the crop to the server with filename crop_<labelId>.png.
    const cropData = {
      label_id: labelId,
      label_type: this.getProperty('labelType'),
      b64: this.getProperty('crop'),
    };
    await fetch('saveImage', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json; charset=UTF-8' },
      body: JSON.stringify(cropData),
    }).then(() => {
      this.setProperty('crop', null); // Remove reference to crop to save memory.
    }).catch((err) => console.error(err));
  }

  /**
   * Rasterizes and caches every label-type icon. renderLabelIcon draws only from this cache, so warming it up front
   * lets the icon, its outline, and any overlay drawn after it (e.g. the severity "?" alert) paint together in the
   * right order — a lazily-loaded icon would instead paint asynchronously, on top of those overlays.
   *
   * Each icon is rasterized once into an offscreen canvas rather than cached as an <img>: the label canvas redraws
   * on every pano move, and re-rasterizing vector art per frame per label is work we can do once instead.
   * @returns {Promise} Resolves once all icons have loaded (or failed) so callers can render with the cache warm.
   */
  static preloadIcons() {
    const iconPaths = util.misc.getIconImagePaths();
    const loads = Object.keys(iconPaths).map(/** @returns {Promise<void>} */ (labelType) => {
      const iconPath = iconPaths[labelType].iconImagePath;
      if (!iconPath || window.labelIconCache[iconPath]) return Promise.resolve();
      return new Promise((resolve) => {
        const imageObj = new Image();
        // The icon SVGs carry only a viewBox, so they have no intrinsic size for the browser to rasterize at; the
        // width/height attributes supply one. Without them Firefox refuses to draw the image to a canvas at all.
        imageObj.width = Label.#ICON_RASTER_SIZE;
        imageObj.height = Label.#ICON_RASTER_SIZE;
        imageObj.onload = () => {
          const raster = document.createElement('canvas');
          raster.width = Label.#ICON_RASTER_SIZE;
          raster.height = Label.#ICON_RASTER_SIZE;
          raster.getContext('2d')
            .drawImage(imageObj, 0, 0, Label.#ICON_RASTER_SIZE, Label.#ICON_RASTER_SIZE);
          window.labelIconCache[iconPath] = raster;
          resolve();
        };
        imageObj.onerror = () => resolve(); // Don't let one missing icon block the rest.
        imageObj.src = iconPath;
      });
    });
    return Promise.all(loads);
  }

  /**
   * Returns a label type's icon as a PNG data URL sized for use as a CSS cursor, or null for types without an icon.
   *
   * Cursors can't take an SVG that has no intrinsic size (and Safari takes no SVG cursor at all), so the vector icon
   * is rasterized to the cursor's exact on-screen size. Results are memoized — the labeling cursor is re-applied on
   * every mousemove.
   * @param {string} labelType
   * @returns {?string}
   */
  static getCursorImageUrl(labelType) {
    if (Label.#cursorUrlCache.has(labelType)) return Label.#cursorUrlCache.get(labelType);

    const iconPath = util.misc.getIconImagePaths(labelType)?.iconImagePath;
    if (!iconPath) {
      Label.#cursorUrlCache.set(labelType, null); // Walk mode and anything else with no marker of its own.
      return null;
    }
    // Not rasterized yet (preloadIcons is still in flight) — leave it uncached so the next mousemove retries.
    const icon = window.labelIconCache[iconPath];
    if (!icon) return null;

    const cursor = document.createElement('canvas');
    cursor.width = Label.CURSOR_ICON_SIZE;
    cursor.height = Label.CURSOR_ICON_SIZE;
    const cursorCtx = cursor.getContext('2d');
    cursorCtx.imageSmoothingQuality = 'high'; // The raster is several times the cursor's size; keep the edge clean.
    cursorCtx.drawImage(icon, 0, 0, Label.CURSOR_ICON_SIZE, Label.CURSOR_ICON_SIZE);
    const url = cursor.toDataURL();
    Label.#cursorUrlCache.set(labelType, url);
    return url;
  }

  /**
   * Draws a label icon and its circular outline. The icon comes from the cache warmed by Label.preloadIcons; the
   * outline is drawn after it so the ring sits on top of the icon's edge. Also draws tutorial example labels.
   * @param {CanvasRenderingContext2D} ctx
   * @param {string} labelType
   * @param {number} x
   * @param {number} y
   * @param {number} radius - The icon's radius in logical px (CanvasFrame.iconRadius).
   */
  static renderLabelIcon(ctx, labelType, x, y, radius) {
    const halfIcon = util.labelIconHalfExtent(radius);
    const size = 2 * halfIcon;
    const icon = window.labelIconCache[util.misc.getIconImagePaths(labelType).iconImagePath];
    if (icon) ctx.drawImage(icon, x - radius + 2, y - radius + 2, size, size);

    // The rings are offsets from the drawn icon rather than fixed radii, and the stroke scales with it too: the icon
    // stops growing once its cap engages (#4838), and an absolute outline would read far heavier against a capped one.
    const k = util.labelIconScale(radius);
    ctx.lineWidth = 0.7 * k;
    ctx.beginPath();
    ctx.arc(x, y, halfIcon - 0.2 * k, 0, 2 * Math.PI);
    ctx.strokeStyle = 'black';
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(x, y, halfIcon + 0.7 * k, 0, 2 * Math.PI);
    ctx.strokeStyle = 'white';
    ctx.stroke();
  }

  /**
   * The minimap marker's accessible name for a label of the given type and era. Earlier eras say so, and drop the
   * "click to review" affordance, because the click only returns to current-mission labels (#2561).
   * @param {string} labelType
   * @param {'current'|'prior'|'outdated'} era
   * @returns {string}
   */
  static minimapMarkerTitle(labelType, era) {
    const labelTypeName = util.misc.labelTypeName(labelType);
    const key = {
      current: 'audit:right-ui.minimap.label-marker-title',
      prior: 'audit:right-ui.minimap.label-marker-title-prior',
      outdated: 'audit:right-ui.minimap.label-marker-title-outdated',
    }[era];
    return i18next.t(key, { labelType: labelTypeName });
  }

  /**
   * Applies an era's look and behavior to a minimap marker: the CSS class that dims earlier passes
   * (.minimap-label-icon--prior / --outdated), the matching accessible name and hover title, whether it is clickable,
   * and its z-order.
   *
   * Only a current-era marker is clickable, because the click only returns to current-mission labels (#2561). A
   * clickable marker is a keyboard-focusable button (Minimap.addMarker), so leaving earlier markers clickable would
   * add a focus stop per earlier label (hundreds on a re-audit) whose activation does nothing.
   *
   * The z-index puts this pass's markers above earlier ones. It is deliberately low: the forward crumbs (20-30) and
   * the peg (1000) set higher ones, so a label marker never hides the navigation it sits on.
   * @param {MinimapMarker} marker
   * @param {string} labelType
   * @param {'current'|'prior'|'outdated'} era
   */
  static styleMinimapMarker(marker, labelType, era) {
    const content = /** @type {HTMLImageElement} */ (marker.content);
    content.className = era === 'current' ? 'minimap-label-icon' : `minimap-label-icon minimap-label-icon--${era}`;
    content.dataset.era = era;
    const title = Label.minimapMarkerTitle(labelType, era);
    content.alt = title;
    marker.setTitle(title);
    marker.setClickable(era === 'current');
    marker.setZIndex(era === 'current' ? 2 : 1);
  }

  /**
   * Creates the marker shown for a label on the minimap.
   * @param {Minimap} minimap - The minimap to put it on.
   * @param {string} labelType
   * @param {{lat: number, lng: number}} latLng
   * @param {'current'|'prior'|'outdated'} [era='current'] - See minimapEra.
   * @param {?(() => void)} [onClick] - What clicking the marker does while its era is current. The tutorial's example
   *                                    labels pass nothing, since there is nowhere to return to.
   * @returns {MinimapMarker}
   */
  static createMinimapMarker(minimap, labelType, latLng, era = 'current', onClick = null) {
    const content = document.createElement('img');
    // Sizing is set in .minimap-label-icon.
    content.src = util.misc.getIconImagePaths(labelType).iconImagePath;
    const marker = minimap.addMarker(latLng, content, { onClick });
    // Class (and so sizing), accessible name, clickability and z-order all depend on the era, so they are set in one
    // place that refreshMinimapEra can re-run.
    Label.styleMinimapMarker(marker, labelType, era);
    return marker;
  }
}

// Set up a global cache for icon images.
if (!window.labelIconCache) {
  window.labelIconCache = {};
}
